import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { ChartBar, ArrowClockwise, Warning, FloppyDisk, ArrowCounterClockwise, MagicWand, Target } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIME_COLORS = {
  TRENDING: { bg: "#10b981", label: "TRENDING", text: "text-emerald-300" },
  RANGING:  { bg: "#06b6d4", label: "RANGING",  text: "text-cyan-300" },
  CHOP:     { bg: "#f59e0b", label: "CHOP",     text: "text-amber-300" },
  PANIC:    { bg: "#ef4444", label: "PANIC",    text: "text-red-300" },
};

const fmtDate = (ms) => {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export function RegimeTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [hover, setHover] = useState(null);
  const [drop, setDrop] = useState(null);       // slider value (null = use engine default)
  const [vol, setVol] = useState(null);
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optResult, setOptResult] = useState(null);
  const [optScope, setOptScope] = useState(1);  // 1=BTC only, 10/25/50 = multi-coin
  const [expandedCoins, setExpandedCoins] = useState(false);
  const [perSymbolCount, setPerSymbolCount] = useState(0);
  const debounceRef = useRef(null);

  // Track how many per-symbol overrides are currently active on the engine
  const refreshPerSymbolCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/status`);
      const map = res.data?.settings?.per_symbol_thresholds || {};
      setPerSymbolCount(Object.keys(map).length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshPerSymbolCount();
    const iv = setInterval(refreshPerSymbolCount, 5000);
    return () => clearInterval(iv);
  }, [refreshPerSymbolCount]);

  const load = useCallback(async (d, dropVal, volVal) => {
    setLoading(true);
    setError(null);
    try {
      const params = { days: d };
      if (dropVal !== null && dropVal !== undefined) params.panic_drop = dropVal;
      if (volVal !== null && volVal !== undefined) params.panic_vol = volVal;
      const res = await axios.get(`${API}/regime/backtest`, { params });
      if (res.data?.ok) {
        setData(res.data);
        // On first non-preview load, seed sliders from engine defaults
        if (!res.data.preview && drop === null) setDrop(res.data.thresholds.btc_panic_drop_pct_1h);
        if (!res.data.preview && vol === null)  setVol(res.data.thresholds.btc_panic_vol_pct);
      } else {
        setError(res.data?.error || "Failed to load");
      }
    } catch (e) {
      setError(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + when days changes: use engine defaults
  useEffect(() => { load(days, null, null); }, [days, load]);

  // Debounced re-fetch when slider values change
  useEffect(() => {
    if (drop === null || vol === null) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(days, drop, vol);
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [drop, vol, days, load]);

  const engineDrop = data?.engine_thresholds?.btc_panic_drop_pct_1h;
  const engineVol  = data?.engine_thresholds?.btc_panic_vol_pct;
  // Live preview flag — recompute from current slider vs engine values so it
  // updates immediately as the user drags (not only after the fetch returns).
  const isPreview = drop !== null && vol !== null && engineDrop !== undefined &&
                    (Number(drop) !== Number(engineDrop) || Number(vol) !== Number(engineVol));

  const resetToEngine = () => {
    if (engineDrop != null) setDrop(engineDrop);
    if (engineVol  != null) setVol(engineVol);
  };

  const saveThresholds = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings`, {
        btc_panic_drop_pct_1h: drop,
        btc_panic_vol_pct: vol,
      });
      toast.success(`Thresholds saved — drop ${drop}% / vol ${vol}%`);
      // Reload to show non-preview
      await load(days, null, null);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const runAutoTune = async () => {
    setOptimizing(true);
    setOptResult(null);
    setExpandedCoins(false);
    try {
      const res = await axios.get(`${API}/regime/optimize`, {
        params: { days, lookahead: 6, top_n: optScope },
        timeout: 60000,
      });
      if (res.data?.ok) {
        setOptResult(res.data);
        const rec = res.data.recommended;
        const scope = res.data.coins_fetched === 1 ? "BTC" : `${res.data.coins_fetched} coins`;
        if (rec) toast.success(`Best on ${scope}: drop ${rec.drop}% / vol ${rec.vol}% (score ${rec.score})`);
      } else {
        toast.error(res.data?.error || "Optimize failed");
      }
    } catch (e) {
      toast.error(e?.message || "Network error");
    } finally {
      setOptimizing(false);
    }
  };

  const applyPair = (d, v) => {
    setDrop(d);
    setVol(v);
    toast.info(`Previewing drop ${d}% / vol ${v}% — press Save to keep`);
  };

  const applyAllPerSymbol = async () => {
    if (!optResult?.per_coin_best?.length) return;
    const thresholds = {};
    optResult.per_coin_best.forEach((row) => {
      thresholds[row.symbol] = { drop: row.drop, vol: row.vol };
    });
    try {
      const res = await axios.post(`${API}/regime/apply-per-symbol`, { thresholds });
      if (res.data?.ok) {
        toast.success(`Applied per-symbol thresholds to ${res.data.applied} coins`);
        setOptResult({ ...optResult, _applied_count: res.data.applied });
        refreshPerSymbolCount();
      } else {
        toast.error(res.data?.error || "Apply failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Apply failed");
    }
  };

  const clearAllPerSymbol = async () => {
    try {
      const res = await axios.delete(`${API}/regime/per-symbol`);
      if (res.data?.ok) {
        toast.success("Per-symbol thresholds cleared — all coins use global");
        setOptResult({ ...optResult, _applied_count: 0 });
        refreshPerSymbolCount();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Clear failed");
    }
  };

  const timeline = data?.timeline || [];
  const total = timeline.length || 1;

  return (
    <div className="border-t border-slate-700/60 bg-[#0B1120]" data-testid="regime-timeline">
      <div className="px-2 md:px-4 py-2 border-b border-slate-700/60 flex items-center justify-between flex-wrap gap-2">
        <span className="label-uppercase text-cyan-300 flex items-center gap-2 text-[10px] md:text-xs">
          <ChartBar size={14} /> <span className="hidden sm:inline">Regime Backtest</span> — Last {days}d
          {isPreview ? (
            <span className="ml-1 md:ml-2 text-[9px] md:text-[10px] px-1.5 py-0.5 border border-amber-500/60 bg-amber-950/40 text-amber-300 rounded font-mono">
              PREVIEW
            </span>
          ) : null}
        </span>
        <div className="flex items-center gap-1 flex-wrap">
          {[7, 14, 30, 60, 90].map((d) => (
            <button
              key={d}
              data-testid={`regime-days-${d}`}
              onClick={() => setDays(d)}
              className={`text-[9px] md:text-[10px] px-1.5 md:px-2 py-1 border rounded ${
                days === d
                  ? "bg-cyan-500/20 border-cyan-400 text-cyan-200"
                  : "border-slate-700 text-slate-400 hover:text-slate-200"
              }`}
            >
              {d}D
            </button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => load(days, drop, vol)}
            data-testid="regime-refresh-btn"
            className="text-slate-400 hover:text-cyan-300 h-7 w-7 p-0 ml-1"
          >
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>

      <div className="px-2 md:px-4 py-3 space-y-3">
        {error ? (
          <div className="flex items-center gap-2 text-amber-400 text-xs" data-testid="regime-error">
            <Warning size={14} /> {error}
          </div>
        ) : null}

        {/* Threshold Tuner */}
        <div className="border border-slate-700/60 rounded p-3 bg-[#0F172A] space-y-3" data-testid="threshold-tuner">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="label-uppercase text-slate-400 text-[10px] flex items-center gap-2">
              Threshold Tuner — drag to what-if
              {perSymbolCount > 0 ? (
                <span className="text-[9px] text-emerald-300 border border-emerald-500/60 px-1.5 py-0.5 rounded normal-case font-mono" data-testid="per-symbol-badge">
                  {perSymbolCount} per-symbol active
                </span>
              ) : null}
            </span>
            <div className="flex items-center gap-1 flex-wrap">
              <div className="flex items-center gap-0.5 mr-1 border border-slate-700 rounded p-0.5" data-testid="scope-selector">
                {[
                  { n: 1,  l: "BTC" },
                  { n: 10, l: "TOP 10" },
                  { n: 25, l: "TOP 25" },
                  { n: 50, l: "TOP 50" },
                ].map((o) => (
                  <button
                    key={o.n}
                    data-testid={`scope-${o.n}`}
                    onClick={() => setOptScope(o.n)}
                    className={`text-[9px] px-1.5 py-0.5 rounded transition
                      ${optScope === o.n
                        ? "bg-cyan-500/20 text-cyan-200"
                        : "text-slate-500 hover:text-slate-300"}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={runAutoTune}
                disabled={optimizing}
                data-testid="threshold-autotune-btn"
                className="text-cyan-300 hover:text-cyan-100 h-7 px-2 text-[10px] border border-cyan-500/40 disabled:opacity-40"
              >
                <MagicWand size={12} className={`mr-1 ${optimizing ? "animate-pulse" : ""}`} />
                {optimizing ? "Sweeping..." : "Auto-tune"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={resetToEngine}
                disabled={!isPreview}
                data-testid="threshold-reset-btn"
                className="text-slate-400 hover:text-cyan-300 h-7 px-2 text-[10px] disabled:opacity-40"
              >
                <ArrowCounterClockwise size={12} className="mr-1" /> Reset
              </Button>
              <Button
                size="sm"
                onClick={saveThresholds}
                disabled={!isPreview || saving}
                data-testid="threshold-save-btn"
                className="h-7 px-2 text-[10px] bg-cyan-500 hover:bg-cyan-400 text-slate-950 disabled:opacity-40"
              >
                <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* BTC 1h drop % slider */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] label-uppercase text-slate-400">BTC 1h Drop → Panic</span>
                <span className="font-mono text-cyan-300 text-xs" data-testid="drop-value">
                  {drop != null ? `${drop.toFixed(1)}%` : "—"}
                  {isPreview && drop !== engineDrop ? (
                    <span className="text-slate-500 ml-2">(engine: {engineDrop}%)</span>
                  ) : null}
                </span>
              </div>
              <Slider
                data-testid="drop-slider"
                min={0.5}
                max={10}
                step={0.1}
                value={drop != null ? [drop] : [3]}
                onValueChange={(v) => setDrop(Number(v[0].toFixed(1)))}
              />
              <div className="flex justify-between text-[9px] text-slate-500 mt-1">
                <span>0.5%</span><span>5%</span><span>10%</span>
              </div>
            </div>

            {/* BTC vol % slider */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] label-uppercase text-slate-400">BTC 1h Vol → Block Entries</span>
                <span className="font-mono text-cyan-300 text-xs" data-testid="vol-value">
                  {vol != null ? `${vol.toFixed(1)}%` : "—"}
                  {isPreview && vol !== engineVol ? (
                    <span className="text-slate-500 ml-2">(engine: {engineVol}%)</span>
                  ) : null}
                </span>
              </div>
              <Slider
                data-testid="vol-slider"
                min={0.5}
                max={10}
                step={0.1}
                value={vol != null ? [vol] : [4]}
                onValueChange={(v) => setVol(Number(v[0].toFixed(1)))}
              />
              <div className="flex justify-between text-[9px] text-slate-500 mt-1">
                <span>0.5%</span><span>5%</span><span>10%</span>
              </div>
            </div>
          </div>

          {/* Auto-tune results */}
          {optResult?.ok ? (
            <div className="border-t border-slate-700/60 pt-3 space-y-2" data-testid="autotune-results">
              <div className="flex items-center gap-2 text-[11px] flex-wrap">
                <Target size={13} className="text-cyan-300" />
                <span className="label-uppercase text-cyan-300">Sweet-Spot Sweep</span>
                <span className="text-slate-500 text-[10px]">
                  {optResult.grid_size} pairs · {optResult.lookahead_hours}h look · {optResult.days}d ·{" "}
                  <button
                    onClick={() => setExpandedCoins(!expandedCoins)}
                    className="underline hover:text-cyan-300"
                    data-testid="coins-toggle"
                  >
                    {optResult.coins_fetched}/{optResult.coins_requested} coins {expandedCoins ? "▲" : "▼"}
                  </button>
                </span>
              </div>

              {expandedCoins && optResult.coins ? (
                <div className="text-[10px] text-slate-400 border border-slate-700/60 rounded p-2 bg-[#0B1120]" data-testid="coins-list">
                  <div className="mb-1 text-slate-500 label-uppercase">Coins evaluated:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {optResult.coins.map((c) => (
                      <span key={c} className="font-mono text-slate-300 bg-slate-800/60 px-1.5 py-0.5 rounded">
                        {c.replace("-USDT-SWAP", "")}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="grid gap-1">
                {optResult.top.map((p, idx) => {
                  const isRec = idx === 0;
                  const isEng = p.drop === optResult.engine.drop && p.vol === optResult.engine.vol;
                  return (
                    <button
                      key={`${p.drop}-${p.vol}-${idx}`}
                      data-testid={`autotune-pair-${idx}`}
                      onClick={() => applyPair(p.drop, p.vol)}
                      className={`flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-2 py-1.5 rounded border text-left text-[10px] md:text-[11px] font-mono transition
                        ${isRec
                          ? "border-emerald-500/60 bg-emerald-950/30 hover:bg-emerald-950/50"
                          : "border-slate-700/60 bg-[#0B1120] hover:bg-slate-800/40"}`}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        {isRec ? (
                          <span className="text-[9px] text-emerald-300 border border-emerald-500/60 px-1.5 py-0.5 rounded whitespace-nowrap">
                            RECOMMENDED
                          </span>
                        ) : (
                          <span className="text-slate-500 w-4">#{idx + 1}</span>
                        )}
                        <span className="text-slate-100 whitespace-nowrap">drop {p.drop}% / vol {p.vol}%</span>
                        {isEng ? <span className="text-[9px] text-cyan-400 whitespace-nowrap">(current)</span> : null}
                      </span>
                      <span className="flex gap-2 md:gap-3 text-slate-400 flex-wrap tabular-nums">
                        <span>score <b className="text-emerald-300">{p.score}</b></span>
                        <span>ev <b className="text-slate-200">{p.events}</b></span>
                        <span>hits <b className="text-emerald-300">{p.tp}</b>/<span className="text-red-300">{p.fp}</span></span>
                        <span>prec <b className="text-slate-200">{(p.precision * 100).toFixed(0)}%</b></span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3">
                <span>Your current: drop {optResult.engine.drop}% / vol {optResult.engine.vol}% → score <b className="text-cyan-300">{optResult.engine.score}</b></span>
                <span>· click any pair to preview on the sliders</span>
                {optResult.top_n > 1 ? (
                  <span className="text-amber-400">· multi-coin score averaged per event across {optResult.coins_fetched} coins</span>
                ) : null}
              </div>

              {/* Per-coin best table */}
              {optResult.per_coin_best?.length > 1 ? (
                <div className="border-t border-slate-700/60 pt-2 mt-2 space-y-1" data-testid="per-coin-best">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <div className="text-[10px] label-uppercase text-slate-400">
                      Per-Coin Best Threshold
                      <span className="text-slate-600 normal-case ml-2">
                        (click a row to preview on sliders — or apply all below)
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={clearAllPerSymbol}
                        data-testid="per-symbol-clear-btn"
                        className="text-slate-400 hover:text-red-300 h-6 px-2 text-[10px]"
                      >
                        <ArrowCounterClockwise size={11} className="mr-1" /> Clear All
                      </Button>
                      <Button
                        size="sm"
                        onClick={applyAllPerSymbol}
                        data-testid="per-symbol-apply-btn"
                        className="h-6 px-2 text-[10px] bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                      >
                        <Target size={11} className="mr-1" /> Apply All to Engine
                      </Button>
                    </div>
                  </div>
                  <div className="hidden md:grid grid-cols-[minmax(60px,1fr)_repeat(6,minmax(0,auto))] gap-x-3 gap-y-0.5 text-[10px] font-mono">
                    <span className="text-slate-500 label-uppercase">Coin</span>
                    <span className="text-slate-500 label-uppercase text-right">Drop</span>
                    <span className="text-slate-500 label-uppercase text-right">Vol</span>
                    <span className="text-slate-500 label-uppercase text-right">Score</span>
                    <span className="text-slate-500 label-uppercase text-right">Ev</span>
                    <span className="text-slate-500 label-uppercase text-right">Prec</span>
                    <span className="text-slate-500 label-uppercase text-right">Δ vs eng</span>
                  </div>
                  <div className="max-h-56 md:max-h-56 overflow-y-auto overflow-x-hidden space-y-0.5" data-testid="per-coin-list">
                    {optResult.per_coin_best.map((row) => {
                      const delta = (row.score ?? 0) - (row.engine_score ?? 0);
                      const isBtc = row.short === "BTC";
                      return (
                        <button
                          key={row.symbol}
                          data-testid={`coin-row-${row.short}`}
                          onClick={() => applyPair(row.drop, row.vol)}
                          className={`w-full rounded border text-left transition
                            md:grid md:grid-cols-[minmax(60px,1fr)_repeat(6,minmax(0,auto))] md:gap-x-3 md:items-center md:px-2 md:py-1 md:text-[11px]
                            px-2 py-1.5 text-[10px]
                            ${isBtc
                              ? "border-cyan-500/40 bg-cyan-950/20 hover:bg-cyan-950/40"
                              : "border-slate-700/40 hover:border-slate-600 hover:bg-slate-800/40"}`}
                        >
                          {/* MOBILE: two-line compact card */}
                          <div className="md:hidden flex items-center justify-between gap-2 font-mono">
                            <span className="text-slate-100 shrink-0">{row.short}</span>
                            <span className="text-cyan-300">
                              {row.drop}%/{row.vol}%
                            </span>
                            <span className="text-emerald-300">{row.score.toFixed(2)}</span>
                            <span
                              className={
                                delta > 0.1 ? "text-emerald-300" : delta < -0.1 ? "text-red-300" : "text-slate-500"
                              }
                            >
                              {delta > 0 ? "+" : ""}{delta.toFixed(2)}
                            </span>
                          </div>
                          <div className="md:hidden mt-0.5 text-[9px] text-slate-500 font-mono flex justify-between">
                            <span>events {row.events}</span>
                            <span>precision {(row.precision * 100).toFixed(0)}%</span>
                          </div>

                          {/* DESKTOP: table row */}
                          <span className="hidden md:inline text-slate-100 font-mono">{row.short}</span>
                          <span className="hidden md:inline text-cyan-300 text-right font-mono">{row.drop}%</span>
                          <span className="hidden md:inline text-cyan-300 text-right font-mono">{row.vol}%</span>
                          <span className="hidden md:inline text-emerald-300 text-right font-mono">{row.score.toFixed(2)}</span>
                          <span className="hidden md:inline text-slate-400 text-right font-mono">{row.events}</span>
                          <span className="hidden md:inline text-slate-300 text-right font-mono">{(row.precision * 100).toFixed(0)}%</span>
                          <span
                            className={`hidden md:inline text-right font-mono ${
                              delta > 0.1 ? "text-emerald-300" : delta < -0.1 ? "text-red-300" : "text-slate-500"
                            }`}
                          >
                            {delta > 0 ? "+" : ""}{delta.toFixed(2)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Timeline strip */}
        <div className="relative">
          <div
            className="flex h-8 w-full rounded overflow-hidden border border-slate-700/60"
            data-testid="regime-strip"
            onMouseLeave={() => setHover(null)}
          >
            {timeline.map((bar, idx) => {
              const cfg = REGIME_COLORS[bar.r] || REGIME_COLORS.RANGING;
              return (
                <div
                  key={idx}
                  onMouseEnter={() => setHover({ ...bar, idx })}
                  style={{ backgroundColor: cfg.bg, width: `${100 / total}%` }}
                  className="h-full transition-opacity hover:opacity-70 cursor-crosshair"
                />
              );
            })}
          </div>
          {hover ? (
            <div className="mt-2 text-[11px] font-mono text-slate-300 flex flex-wrap gap-x-4 gap-y-1" data-testid="regime-hover">
              <span className={REGIME_COLORS[hover.r]?.text || "text-slate-300"}>
                <b>{hover.r}</b>
              </span>
              <span>{fmtDate(hover.t)}</span>
              <span>BTC ${hover.c.toLocaleString()}</span>
              <span>ADX {hover.adx}</span>
              <span>Vol {hover.vol}%</span>
              <span>Δ1h {hover.ret > 0 ? "+" : ""}{hover.ret}%</span>
            </div>
          ) : (
            <div className="mt-2 text-[10px] text-slate-500">
              Hover the strip to inspect any bar. Each block ≈ 1h.
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {["TRENDING", "RANGING", "CHOP", "PANIC"].map((r) => {
            const cfg = REGIME_COLORS[r];
            const pct = data?.pct?.[r] ?? 0;
            const n = data?.counts?.[r] ?? 0;
            return (
              <div
                key={r}
                data-testid={`regime-stat-${r}`}
                className="border border-slate-700/60 rounded px-2 py-1.5 bg-[#0F172A]"
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: cfg.bg }} />
                    <span className={`text-[10px] ${cfg.text} font-mono tracking-widest`}>{r}</span>
                  </span>
                  <span className="text-slate-500 text-[10px] font-mono">{n}h</span>
                </div>
                <div className="text-lg font-mono text-slate-100 leading-tight">{pct}%</div>
              </div>
            );
          })}
        </div>

        {/* Bot behavior summary */}
        <div className="text-[11px] text-slate-400 flex flex-wrap gap-x-4 gap-y-1" data-testid="regime-summary">
          <span>
            Bot would have <b className="text-emerald-300">traded</b>{" "}
            <span className="font-mono">{data?.allowed_bars ?? 0}h</span>
          </span>
          <span>
            <b className="text-red-300">paused / closed longs</b>{" "}
            <span className="font-mono">{data?.paused_bars ?? 0}h</span>{" "}
            ({(data?.panic_events?.length ?? 0)} PANIC events)
          </span>
          {data?.thresholds ? (
            <span className="text-slate-500">
              Using: drop&nbsp;≥&nbsp;{data.thresholds.btc_panic_drop_pct_1h}% / vol&nbsp;≥&nbsp;{data.thresholds.btc_panic_vol_pct}%
            </span>
          ) : null}
        </div>

        {/* Recent PANIC events */}
        {data?.panic_events?.length ? (
          <div className="border border-red-500/30 bg-red-950/20 rounded p-2">
            <div className="text-[10px] label-uppercase text-red-300 mb-1">Recent Panic Events (would auto-close longs)</div>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {data.panic_events.slice().reverse().map((e, i) => (
                <div key={i} className="text-[11px] font-mono text-slate-300 flex justify-between gap-3">
                  <span>{fmtDate(e.t)}</span>
                  <span>BTC ${e.close.toLocaleString()}</span>
                  <span className={e.ret_1h < 0 ? "text-red-300" : "text-emerald-300"}>
                    Δ1h {e.ret_1h > 0 ? "+" : ""}{e.ret_1h}%
                  </span>
                  <span className="text-amber-300">vol {e.vol}%</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default RegimeTimeline;
